/**
 * Bulk Prefix Editor Component
 *
 * Shared component for setting document numbering prefixes in bulk
 * Used in both Company Setup wizard and standalone Document Numbering page
 */

import { useState } from 'react'

import { Input, Button } from '../ui'

interface BulkPrefixEditorProps {
  onApply: (draftPrefix: string, finalPrefix: string) => void
  onCancel: () => void
  isSaving?: boolean
  title?: string
  description?: string
  applyButtonText?: string
}

function BulkPrefixHeader({ title, isSaving, onCancel }: { title: string; isSaving: boolean; onCancel: () => void }): JSX.Element {
  return (
    <div className="bg-muted px-6 py-4 border-b border-border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">⚡</span>
          <h3 className="font-semibold text-foreground text-lg">{title}</h3>
        </div>
        <button className="text-muted-foreground hover:text-foreground" disabled={isSaving} onClick={onCancel}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
        </button>
      </div>
    </div>
  )
}

function PrefixInput({ id, label, value, placeholder, preview, isSaving, onChange }: {
  id: string; label: string; value: string; placeholder: string; preview: string; isSaving: boolean; onChange: (v: string) => void
}): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor={id}>{label}</label>
      <Input className="font-mono text-lg" disabled={isSaving} id={id} maxLength={5} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />
      <p className="text-xs text-muted-foreground mt-1">{preview}</p>
    </div>
  )
}

export function BulkPrefixEditor({
  onApply, onCancel, isSaving = false,
  title = 'Quick Setup All Series',
  description = 'This will create 10 series: Draft and final numbering for B2B, Export, B2C, Credit Notes, and Debit Notes.',
  applyButtonText = '✓ Create All 10 Series',
}: BulkPrefixEditorProps): JSX.Element {
  const [bulkDraftPrefix, setBulkDraftPrefix] = useState('P')
  const [bulkFinalPrefix, setBulkFinalPrefix] = useState('')
  const fp = bulkFinalPrefix

  return (
    <div className="bg-surface rounded-lg shadow-sm border-2 border-border overflow-hidden">
      <BulkPrefixHeader isSaving={isSaving} title={title} onCancel={onCancel} />
      <div className="p-6 flex flex-col el-gap-field">
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200"><strong>How it works:</strong> {description}</p>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">Example: Draft pattern &quot;PRO&quot; creates PROB, PROE, PROC, PROCN, PRODN</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
          <PrefixInput id="bulk-draft-prefix" isSaving={isSaving} label="Draft Prefix Pattern" placeholder="P" preview={`Preview: ${bulkDraftPrefix}B, ${bulkDraftPrefix}E, ${bulkDraftPrefix}C, ${bulkDraftPrefix}CN, ${bulkDraftPrefix}DN`}
            value={bulkDraftPrefix}
            onChange={setBulkDraftPrefix}
          />
          <PrefixInput id="bulk-final-prefix" isSaving={isSaving} label="Final Prefix Pattern (optional)" placeholder="Leave empty for B, E, C, CN, DN" preview={`Preview: ${fp || 'B'}, ${fp || 'E'}, ${fp || 'C'}, ${fp || 'CN'}, ${fp || 'DN'}`}
            value={bulkFinalPrefix}
            onChange={setBulkFinalPrefix}
          />
        </div>
        <div className="flex el-gap-field pt-4">
          <Button className="flex-1" disabled={isSaving} variant="primary" onClick={() => onApply(bulkDraftPrefix, bulkFinalPrefix)}>
            {isSaving ? 'Processing...' : applyButtonText}
          </Button>
          <Button disabled={isSaving} variant="secondary" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </div>
  )
}

/**
 * Utility function to generate prefixes from patterns
 */
export function generatePrefixes(draftPattern: string, finalPattern: string): Record<string, { draft: string; final: string }> {
  const docTypes: Array<{ type: string; suffix: string }> = [
    { type: 'b2b', suffix: 'B' },
    { type: 'export', suffix: 'E' },
    { type: 'b2c', suffix: 'C' },
    { type: 'creditNote', suffix: 'CN' },
    { type: 'debitNote', suffix: 'DN' },
  ]
  const prefixes: Record<string, { draft: string; final: string }> = {}
  docTypes.forEach(({ type, suffix }) => {
    prefixes[type] = { draft: draftPattern ? `${draftPattern}${suffix}` : suffix, final: finalPattern || suffix }
  })
  return prefixes
}
